import { FirebaseError } from "firebase/app";
import type { TFunction } from "i18next";

type LoginCodeErrorDetails = {
  reason?: unknown;
  channel?: unknown;
  retryAfterSeconds?: unknown;
  attemptsRemaining?: unknown;
};

function detailsFrom(error: FirebaseError): LoginCodeErrorDetails {
  const { details } = error as FirebaseError & { details?: unknown };
  return typeof details === "object" && details !== null
    ? (details as LoginCodeErrorDetails)
    : {};
}

/** Translate stable callable error metadata; never render server text verbatim. */
export default function loginCodeErrorMessage(
  error: FirebaseError,
  t: TFunction,
  phase: "request" | "verify"
): string {
  const details = detailsFrom(error);
  switch (details.reason) {
    case "format":
      return phase === "verify"
        ? t("Enter the 6-digit code we sent you.")
        : t(
            "That doesn't look like an email or phone number. Check it and try again."
          );
    case "not_found":
      return details.channel === "phone"
        ? t(
            "We couldn't find an account with that phone number. Check the number, or register to get started."
          )
        : t(
            "We couldn't find an account with that email. Check the spelling, or register to get started."
          );
    case "disabled":
      return t(
        "This account is disabled. Contact the Emerge team if you need help."
      );
    case "use_email":
      return t(
        "We can't safely text a code to that number. Sign in with your email instead."
      );
    case "delivery_unavailable":
      return t("Code sign-in is busy right now. Please try again shortly.");
    case "rate_limited": {
      const seconds =
        typeof details.retryAfterSeconds === "number"
          ? details.retryAfterSeconds
          : 60;
      const minutes = Math.max(Math.ceil(seconds / 60), 1);
      return minutes === 1
        ? t("Too many code requests. Try again in 1 minute.")
        : t("Too many code requests. Try again in {{minutes}} minutes.", {
            minutes,
          });
    }
    case "request_in_progress":
      return t("A code is already being sent. Wait a moment and try again.");
    case "code_expired":
      return t("That code has expired. Request a new one.");
    case "too_many_attempts": {
      // Window-scoped budget: a new code will not clear it, so don't suggest one.
      const seconds =
        typeof details.retryAfterSeconds === "number"
          ? details.retryAfterSeconds
          : 60;
      const minutes = Math.max(Math.ceil(seconds / 60), 1);
      return minutes === 1
        ? t("Too many tries. Try again in 1 minute.")
        : t("Too many tries. Try again in {{minutes}} minutes.", { minutes });
    }
    case "code_mismatch": {
      const attempts =
        typeof details.attemptsRemaining === "number"
          ? details.attemptsRemaining
          : 0;
      return attempts === 1
        ? t("That code didn't match. 1 try left.")
        : t("That code didn't match. {{attempts}} tries left.", {
            attempts,
          });
    }
    case "verification_in_progress":
      return t(
        "Another sign-in attempt is finishing. Wait a moment and try again."
      );
    default:
      if (error.code === "functions/not-found" && phase === "request") {
        return t(
          "We couldn't find an account with that email or phone number. Check it, or register to get started."
        );
      }
      if (error.code === "functions/unauthenticated") {
        return t(
          "We couldn't verify this device. Refresh the page and try again."
        );
      }
      return t("We couldn't complete that request. Please try again.");
  }
}
